import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Field, FieldDescription, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";

/** Dollar string → integer cents; null if invalid (>2 decimals, negative). */
function dollarsToCents(input: string): number | null {
	const s = input.trim();
	if (s === "") return 0;
	if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
	return Math.round(Number.parseFloat(s) * 100);
}

function centsToDollars(cents: number): string {
	return cents === 0 ? "" : (cents / 100).toFixed(2);
}

/** Staff form: set a firm's one-time per-seat onboarding fee. */
export default function FirmOnboardingFeeCard({
	tenantId,
	currentFeeCents,
}: {
	tenantId: Id<"tenants">;
	currentFeeCents: number;
}) {
	const setFee = useMutation(api.staff.setFirmOnboardingFee);
	const [value, setValue] = useState(centsToDollars(currentFeeCents));
	const [saved, setSaved] = useState(false);

	const cents = dollarsToCents(value);
	const invalid = cents === null;
	const dirty = !invalid && cents !== currentFeeCents;

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async () => {
			if (cents === null) throw new Error("Enter a valid dollar amount");
			await setFee({ tenantId, onboardingFeePerSeatCents: cents });
		},
		onSuccess: () => setSaved(true),
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle>Onboarding fee</CardTitle>
			</CardHeader>
			<CardContent>
				<form
					className="flex flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						mutate();
					}}
				>
					<Field>
						<FieldLabel htmlFor="onboarding-fee">Fee per seat</FieldLabel>
						<Input
							id="onboarding-fee"
							inputMode="decimal"
							value={value}
							onChange={(e) => {
								setValue(e.target.value);
								setSaved(false);
							}}
							placeholder="0.00"
						/>
						<FieldDescription>
							One-time charge per seat, billed when this firm adds capacity
							(initial subscribe and each new seat). Dollars; blank or 0 = no
							fee.
						</FieldDescription>
					</Field>
					{invalid && value.trim() !== "" ? (
						<p className="text-sm text-destructive">
							Enter dollars with at most two decimals.
						</p>
					) : null}
					{error ? (
						<p className="text-sm text-destructive">
							{error instanceof Error ? error.message : String(error)}
						</p>
					) : null}
					<div className="flex items-center gap-3">
						<Button type="submit" disabled={isPending || !dirty}>
							{isPending ? "Saving..." : "Save"}
						</Button>
						{saved && !dirty ? (
							<span className="text-sm text-muted-foreground">Saved</span>
						) : null}
					</div>
				</form>
			</CardContent>
		</Card>
	);
}
